import { useEffect, useMemo, useRef, useState } from 'react';
import { RegistrationModalShell, MultiSelect, SingleRangeSlider, TuitionModeSelector, WeeklyTimeTable } from '@tutor/ui';
import { toast } from 'sonner';
import { STATE_DISTRICTS, STATE_OPTIONS } from '@tutor/types';
import { createCase } from '@/api/services/casesApi';
import { useAuthContext } from '@/contexts/useAuthContext';
import { useModal } from '@/contexts/useModal';
import { getErrorMessage } from '@/api/errorHandler';
import { clearPendingRegistration, getPendingRegistration, savePendingRegistration } from '@/utils/storage';
import { parseMusicParentStep } from '@/schemas/musicRegistrationSchemas';
import type { MusicParentRegistrationFormData } from '@/types/musicRegistration';
import TooltipIcon from '@/components/common/TooltipIcon';
import {
  MUSIC_EXAM_BOARDS, MUSIC_LESSON_SUBJECTS, MUSIC_LEARNING_GOALS, MUSIC_LEVELS,
  MUSIC_LESSON_FORMATS, MUSIC_PHYSICAL_VENUES, MUSIC_TEACHING_LANGUAGES,
} from '@/constants/musicRegistrationOptions';
import { ChoiceButtons, DropdownInput, Field, StepGrid, TextArea, TextInput } from './MusicFormControls';
import { useNavigate } from 'react-router-dom';
import { useOptionalI18n } from '@/i18n';
import { localizeOptions, localizeStringOptions, localizeText } from '@/i18n/options';
import { getWeeklyTimeTableI18nProps } from '@/utils/weeklyTimeTableI18n';
import { completeMusicRegistrationSession } from '@/utils/musicRegistrationWorkflow';
import { useLocalizedPath } from '@/i18n/navigation';

const steps = [
  { id: 'setup', label: 'Lesson setup' },
  { id: 'student', label: 'Student & lesson' },
  { id: 'schedule', label: 'Schedule & location' },
  { id: 'review', label: 'Budget & requirements' },
];

const LESSON_DURATION_MINUTES = [30, 45, 60, 90, 120];
const hoursToMinutes = (hours: number) => Math.round(hours * 60);
const minutesToHours = (minutes: number) => minutes / 60;

const initialData: MusicParentRegistrationFormData = {
  instrument: '', studentAge: '', musicLevel: '', learningGoal: '', examBoard: '', hasOwnInstrument: null,
  tuitionType: '', lessonFormat: '', physicalVenue: '', teachingLanguages: [], lessonsPerWeek: 1,
  lessonDuration: 1, state: '', district: '', areaNote: '', preferredTimes: {}, hourlyRate: 60,
  notes: '', termsAccepted: false,
};

const getErrors = (result: ReturnType<typeof parseMusicParentStep>, locale: Parameters<typeof localizeText>[0]) => {
  if (result.success) return {};
  const fieldErrors: Record<string, string> = {};
  result.error.issues.forEach((issue) => {
    const field = String(issue.path[0]);
    if (field && !fieldErrors[field]) fieldErrors[field] = localizeText(locale, issue.message);
  });
  return fieldErrors;
};

interface MusicParentRegistrationProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  presentation?: 'modal' | 'page';
  showPageBackButton?: boolean;
  successFeedback?: 'toast' | 'none';
}

const MusicParentRegistrationModal = ({
  isOpen,
  onClose,
  onCompleted,
  presentation = 'modal',
  showPageBackButton,
  successFeedback = 'toast',
}: MusicParentRegistrationProps) => {
  const navigate = useNavigate();
  const localizePath = useLocalizedPath();
  const { locale } = useOptionalI18n();
  const l = (text: string) => localizeText(locale, text);
  const weeklyTimeTableI18n = getWeeklyTimeTableI18nProps(locale);
  const instruments = localizeStringOptions(locale, MUSIC_LESSON_SUBJECTS);
  const levels = localizeStringOptions(locale, MUSIC_LEVELS);
  const lessonFormats = localizeOptions(locale, MUSIC_LESSON_FORMATS);
  const physicalVenues = localizeOptions(locale, MUSIC_PHYSICAL_VENUES);
  const stateOptions = localizeOptions(locale, STATE_OPTIONS);
  const teachingLanguages = localizeStringOptions(locale, MUSIC_TEACHING_LANGUAGES);
  const { backendUser, profile } = useAuthContext();
  const { openLoginModal, openCloseWarning } = useModal();
  const [data, setData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const errorsRef = useRef<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldResumeSubmission, setShouldResumeSubmission] = useState(false);
  const isDirty = useMemo(() => JSON.stringify(data) !== JSON.stringify(initialData), [data]);
  const update = (values: Partial<MusicParentRegistrationFormData>) => setData((current) => ({ ...current, ...values }));

  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  // Match Academic registration: only refresh errors already shown, and remove
  // each one as soon as that field becomes valid without revealing new errors.
  useEffect(() => {
    const currentErrors = errorsRef.current;
    if (Object.keys(currentErrors).length === 0) return;

    const result = parseMusicParentStep(currentStep, data);
    const failingFields = getErrors(result, locale);
    const refreshedErrors = Object.fromEntries(
      Object.keys(currentErrors)
        .filter((field) => failingFields[field])
        .map((field) => [field, failingFields[field]]),
    );

    if (JSON.stringify(refreshedErrors) !== JSON.stringify(currentErrors)) {
      setErrors(refreshedErrors);
    }
  }, [currentStep, data, locale]);

  useEffect(() => {
    if (!isOpen) return;
    const pending = getPendingRegistration();
    if (pending?.type === 'music_parent') {
      setData({ ...initialData, ...pending.formData } as MusicParentRegistrationFormData);
      setCurrentStep(steps.length - 1);
      setShouldResumeSubmission(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isOpen) {
      window.__devPanel?.unregister('music_parent');
      return;
    }
    window.__devPanel?.register({
      type: 'music_parent',
      setFormData: (value) => setData(value as MusicParentRegistrationFormData),
      setCurrentStep,
      stepCount: steps.length,
      get currentStep() { return currentStep; },
    });
    return () => window.__devPanel?.unregister('music_parent');
  }, [currentStep, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      window.__debugBridge?.unregisterFormData('music_parent');
      return;
    }
    window.__debugBridge?.registerFormData('music_parent', () => ({
      formData: data as unknown as Record<string, unknown>,
      validationErrors: errors,
      currentStep,
      stepCount: steps.length,
    }));
    return () => window.__debugBridge?.unregisterFormData('music_parent');
  }, [currentStep, data, errors, isOpen]);

  const validate = () => {
    const result = parseMusicParentStep(currentStep, data);
    const nextErrors = getErrors(result, locale);
    setErrors(nextErrors);
    return result.success;
  };

  const safeClose = () => {
    if (isDirty && !isSubmitting) {
      openCloseWarning({ title: l('Unsaved music request'), message: l('Discard the information entered in this form?'), cancelText: l('Continue editing'), confirmText: l('Discard'), onConfirm: onClose });
      return;
    }
    onClose();
  };

  const submit = async () => {
    if (!validate()) return;
    if (backendUser && (profile?.role ?? backendUser.currentRole) !== 'parent') {
      toast.error(l('Please switch to a Parent/Student account before submitting.'));
      return;
    }
    if (!backendUser) {
      savePendingRegistration('music_parent', data as unknown as Record<string, unknown>, presentation);
      if (presentation === 'page') {
        navigate('/music/register/auth');
        return;
      }
      onClose();
      openLoginModal({ initialMode: 'signup', initialUserType: 'parent', fromRegistration: true, registrationService: 'music' });
      return;
    }
    setIsSubmitting(true);
    try {
      await createCase({
        serviceType: 'music', tuitionType: data.tuitionType as 'physical' | 'online' | 'both',
        teachingLanguages: data.teachingLanguages, lessonsPerWeek: data.lessonsPerWeek,
        lessonDuration: data.lessonDuration, hourlyRate: data.hourlyRate,
        state: data.tuitionType === 'online' ? undefined : data.state,
        district: data.tuitionType === 'online' ? undefined : data.district,
        areaNote: data.tuitionType === 'online' ? undefined : data.areaNote || undefined,
        preferredTimes: data.preferredTimes,
        musicDetails: {
          instrument: data.instrument, studentAge: Number(data.studentAge), musicLevel: data.musicLevel,
          learningGoal: data.learningGoal, lessonFormat: data.lessonFormat,
          physicalVenue: data.tuitionType === 'online' ? undefined : data.physicalVenue || undefined,
          hasOwnInstrument: data.hasOwnInstrument ?? undefined, examBoard: data.examBoard || undefined,
          preferredDuration: data.lessonDuration, notes: data.notes || undefined,
        },
      });
      clearPendingRegistration();
      if (successFeedback === 'toast') {
        toast.success(l('Your music tutor request has been submitted.'));
      }
      setData(initialData);
      setCurrentStep(0);
      await completeMusicRegistrationSession();
      await (onCompleted ?? onClose)();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const activeRole = profile?.role ?? backendUser?.currentRole;
    if (!shouldResumeSubmission || !backendUser || activeRole !== 'parent' || isSubmitting) return;
    setShouldResumeSubmission(false);
    void submit();
  // The resume flag guarantees this runs once after authenticated state and restored data are ready.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUser, data, isSubmitting, profile, shouldResumeSubmission]);

  return (
    <RegistrationModalShell isOpen={isOpen} onClose={safeClose} title={l('Find a Music Tutor')} steps={steps.map((step) => ({ ...step, label: l(step.label) }))} currentStep={currentStep} onBack={() => { setErrors({}); setCurrentStep((s) => Math.max(0, s - 1)); }} onNext={() => { if (validate()) { setErrors({}); setCurrentStep((s) => s + 1); } }} onSubmit={submit} isSubmitting={isSubmitting} submitLabel={l('Submit request')} backLabel={l('Back')} continueLabel={l('Continue')} pageBackLabel={l('Registration home')} themeClassName="theme-music" presentation={presentation} showPageBackButton={showPageBackButton}>
      {currentStep === 0 && <div className="space-y-6">
        <Field label={l('Lesson mode')} error={errors.tuitionType}><TuitionModeSelector value={data.tuitionType} onChange={(tuitionType) => update({ tuitionType })} labels={{ physical: l('Physical Tuition'), online: l('Online Tuition'), both: l('Both'), selected: l('Selected') }} /></Field>
        <Field label={l('Lesson format')} error={errors.lessonFormat}><ChoiceButtons options={lessonFormats} value={data.lessonFormat} onChange={(value) => update({ lessonFormat: value })} /></Field>
        {data.tuitionType !== 'online' && <Field label={l('Physical lesson venue (optional)')} hint={l('Leave blank if you have no preference.')}><DropdownInput searchable={false} options={[{ value: '', label: l('No preference') }, ...physicalVenues]} value={data.physicalVenue} onChange={(value) => update({ physicalVenue: value as MusicParentRegistrationFormData['physicalVenue'] })} placeholder={l('No preference')} /></Field>}
      </div>}
      {currentStep === 1 && <div className="space-y-6">
        <StepGrid>
          <Field label={l('Instrument')} error={errors.instrument}><DropdownInput options={instruments} value={data.instrument} onChange={(value) => update({ instrument: String(value) })} placeholder={l('Select instrument')} allowCustom /></Field>
          <Field label={l('Student age')} error={errors.studentAge}><TextInput type="number" min={1} max={100} value={data.studentAge} onChange={(e) => update({ studentAge: e.target.value ? Number(e.target.value) : '' })} /></Field>
          <Field label={l('Current music level')} error={errors.musicLevel}><DropdownInput searchable={false} options={levels} value={data.musicLevel} onChange={(value) => update({ musicLevel: String(value) })} placeholder={l('Select level')} /></Field>
          <Field label={l('Learning goal (optional)')} error={errors.learningGoal}><DropdownInput searchable={false} options={[{ value: '', label: l('No preference') }, ...localizeStringOptions(locale, MUSIC_LEARNING_GOALS)]} value={data.learningGoal} onChange={(value) => update({ learningGoal: String(value) })} placeholder={l('No preference')} /></Field>
          <div className="space-y-2"><div className="flex min-h-7 items-center gap-2"><label className="text-sm font-semibold text-gray-800">{l('Exam board (optional)')}</label><TooltipIcon label={l('What is an exam board?')}><p>{l('Select this only if the student is preparing for a graded music exam, such as ABRSM, Trinity College London, or Rockschool.')}</p></TooltipIcon></div><DropdownInput options={localizeStringOptions(locale, MUSIC_EXAM_BOARDS)} value={data.examBoard} onChange={(value) => update({ examBoard: String(value) })} placeholder={l('Select or enter an exam board')} allowCustom />{errors.examBoard && <p role="alert" className="text-sm font-medium text-red-600">{errors.examBoard}</p>}</div>
          <Field label={l('Does the student own the instrument?')} error={errors.hasOwnInstrument}><ChoiceButtons options={[{ value: 'yes', label: l('Yes') }, { value: 'no', label: l('No') }]} value={data.hasOwnInstrument === null ? '' : data.hasOwnInstrument ? 'yes' : 'no'} onChange={(v) => update({ hasOwnInstrument: v === 'yes' })} /></Field>
        </StepGrid>
      </div>}
      {currentStep === 2 && <div className="space-y-6">
        <StepGrid>
          <Field label={l('Teaching languages')} error={errors.teachingLanguages}><MultiSelect searchable={false} options={teachingLanguages} value={data.teachingLanguages} onChange={(teachingLanguages) => update({ teachingLanguages })} /></Field>
          <Field label={l('Lessons per week')}><DropdownInput searchable={false} options={[1,2,3,4,5,6,7].map((value) => ({ value, label: String(value) }))} value={data.lessonsPerWeek} onChange={(value) => update({ lessonsPerWeek: Number(value) })} /></Field>
          <Field label={l('Lesson duration')}><DropdownInput searchable={false} options={LESSON_DURATION_MINUTES.map((value) => ({ value, label: `${value} ${l('minutes')}` }))} value={hoursToMinutes(data.lessonDuration)} onChange={(value) => update({ lessonDuration: minutesToHours(Number(value)) })} /></Field>
        </StepGrid>
        {data.tuitionType !== 'online' && <section className="space-y-5 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900">{l('Lesson location')}</h3>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label={l('State')} error={errors.state}><DropdownInput options={stateOptions} value={data.state} onChange={(value) => update({ state: String(value), district: '', areaNote: '' })} placeholder={l('Select state')} /></Field>
            <Field label={l('District')} error={errors.district}><DropdownInput disabled={!data.state} options={localizeStringOptions(locale, STATE_DISTRICTS[data.state as keyof typeof STATE_DISTRICTS] ?? [])} value={data.district} onChange={(value) => update({ district: String(value) })} placeholder={l('Select district')} /></Field>
            <div className="space-y-2 md:col-span-2">
              <div className="flex min-h-7 items-center gap-2">
                <span className="text-sm font-semibold text-gray-800">{l('Specific Areas / Neighbourhoods (Optional)')}</span>
                <TooltipIcon label={l('Why share a general area?')}>
                  <p>{l('Share a general area or neighbourhood, not your full address. This helps us match you with suitable nearby tutors more accurately while protecting your privacy.')}</p>
                </TooltipIcon>
              </div>
              <TextInput maxLength={80} value={data.areaNote} onChange={(e) => update({ areaNote: e.target.value })} />
              {errors.areaNote && <p role="alert" className="text-sm font-medium text-red-600">{errors.areaNote}</p>}
            </div>
          </div>
        </section>}
        <div><WeeklyTimeTable selectedTimes={data.preferredTimes} onChange={(preferredTimes) => update({ preferredTimes })} label={l('Preferred times')} helperText={l('Click on the grid to select your preferred teaching times')} {...weeklyTimeTableI18n} />{errors.preferredTimes && <p role="alert" className="mt-2 text-sm font-medium text-red-600">{errors.preferredTimes}</p>}</div>
      </div>}
      {currentStep === 3 && <div className="space-y-6">
        <div className="space-y-2 rounded-xl border border-primary-200 bg-primary-50/70 p-4"><div className="flex min-h-7 items-center justify-between gap-3"><span className="text-sm font-semibold text-primary-800">{l('Maximum hourly budget')} <span className="text-red-500">*</span></span><a href={localizePath('/music/price')} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-md text-sm font-semibold text-primary-700 underline underline-offset-4 hover:text-primary-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">{l('View music rate guide')}</a></div><SingleRangeSlider min={10} max={800} value={data.hourlyRate} onChange={(hourlyRate) => update({ hourlyRate })} unit="RM" colorTheme="primary" />{errors.hourlyRate && <p role="alert" className="text-sm font-medium text-red-600">{errors.hourlyRate}</p>}</div>
        <div className="space-y-2">
          <div className="flex min-h-7 items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">{l('Remarks (optional)')}</span>
            <TooltipIcon label={l('What can I include in remarks?')}>
              <p>{l('Briefly add learning preferences, scheduling constraints, or other details that may help the tutor prepare. Keep it short.')}</p>
            </TooltipIcon>
          </div>
          <TextArea rows={4} maxLength={500} value={data.notes} onChange={(e) => update({ notes: e.target.value })} />
          {errors.notes && <p role="alert" className="text-sm font-medium text-red-600">{errors.notes}</p>}
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 p-4"><input type="checkbox" checked={data.termsAccepted} onChange={(e) => update({ termsAccepted: e.target.checked })} className="mt-1 h-4 w-4 accent-primary" /><span className="text-sm text-gray-700">{l('I agree to the')} <a href="/terms-tutors" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline hover:text-primary-600">{l('Terms of Service')}</a> {l('and')} <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline hover:text-primary-600">{l('Privacy Policy')}</a>{l('.')}</span></label>{errors.termsAccepted && <p role="alert" className="text-sm text-red-600">{errors.termsAccepted}</p>}
      </div>}
    </RegistrationModalShell>
  );
};

export default MusicParentRegistrationModal;
